import React, { useEffect, useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

// #region Components
import Input from "shared/components/FormElements/Input";
import Button from "shared/components/FormElements/Button";
import Card from "shared/components/UIElements/Card";
import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";
import ErrorModal from "shared/components/UIElements/ErrorModal";
import Modal from "shared/components/UIElements/Modal";
// #endregion Components

// #region Utils
import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";
// #endregion Utils

// #region Hooks
import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { StoreContext } from "shared/context/store-context";
// #endregion Hooks

import "./Item.css";

const TransferRegionItem = (props) => {
  const auth = useContext(AuthContext);
  const store = useContext(StoreContext);

  const [itemstate, setItemState] = useState();
  const [loadedItem, setLoadedItem] = useState();
  const [openError, setOpenError] = React.useState(false);

  const itemId = props.itemid;
  const updateStoreItems = store.UpdateIkarusTransferRegions;

  const ItemTypeTR = "Transfer Bölgesi";
  const ItemType = "region";
  const ItemEndPointType = "ikarustransferregions";

  // #region Standard Interface functions
  const { isLoading, error, sendRequest, clearError } = useHttpClient();
  const navigate = useNavigate();

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const showDeleteWarningHandler = () => {
    setShowConfirmModal(true);
  };

  const cancelDeleteHandler = () => {
    setShowConfirmModal(false);
  };
  // #endregion Standard Interface functions

  // #region Standard API calls
  const fetchItem = useCallback(async () => {
    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/${ItemEndPointType}/${itemId}`);
      setLoadedItem(responseData[ItemType]);
      setItemState(Date.now());
    } catch (err) {}
  }, [sendRequest, itemId]);

  const confirmDeleteHandler = async () => {
    setShowConfirmModal(false);
    try {
      await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/${ItemEndPointType}/${itemId}`, "DELETE", null, {
        "Content-Type": "application/json",
      });
      setItemState(Date.now());
      updateStoreItems();
      setLoadedItem(null);
      navigate(`/${ItemEndPointType}`);
    } catch (err) {}
  };
  // #endregion Standard API calls

  useEffect(() => {
    fetchItem();
  }, [itemId, fetchItem]);

  const [formState, inputHandler] = useForm(
    {
      name: { value: "", isValid: false },
      name_EN: { value: "", isValid: false },
      name_RU: { value: "", isValid: false },
      amount: { value: "", isValid: false },
      amount_VIP: { value: "", isValid: false },
      amount_5_10: { value: "", isValid: false },
    },
    true
  );

  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();

    let amounttoWrite = 0;
    let amounttoWrite_VIP = 0;
    let amounttoWrite_5_10 = 0;
    //check if amount is number
    if (
      isNaN(formState.inputs.amount.value) ||
      isNaN(formState.inputs.amount_VIP.value) ||
      isNaN(formState.inputs.amount_5_10.value)
    ) {
      setOpenError(true);
      return;
    } else {
      amounttoWrite = parseInt(formState.inputs.amount.value);
      amounttoWrite_VIP = parseInt(formState.inputs.amount_VIP.value);
      amounttoWrite_5_10 = parseInt(formState.inputs.amount_5_10.value);
    }

    let responseData;
    try {
      responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/${ItemEndPointType}/${itemId}`,
        "PATCH",
        JSON.stringify({
          name: formState.inputs.name.value,
          name_EN: formState.inputs.name_EN.value,
          name_RU: formState.inputs.name_RU.value,
          amount: amounttoWrite,
          amount_VIP: amounttoWrite_VIP,
          amount_5_10: amounttoWrite_5_10,
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        }
      );

      setLoadedItem(responseData[ItemType]);
      setItemState(Date.now());
      updateStoreItems();

      //history.push(`/${ItemEndPointType}`);
    } catch (err) {}
  };

  if (isLoading) {
    return (
      <div className="center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!loadedItem && !error) {
    return (
      <div className="center">
        <Card>
          <h2>{ItemTypeTR} bulunamadı.</h2>
        </Card>
      </div>
    );
  }

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenError(false);
  };

  return (
    <React.Fragment>
      <ErrorModal error={error} onClear={clearError} />
      <Modal
        show={showConfirmModal}
        onCancel={cancelDeleteHandler}
        header="Emin misiniz?"
        footerClass="place-item__modal-actions"
        footer={
          <React.Fragment>
            <Button inverse onClick={cancelDeleteHandler}>
              Hayır
            </Button>
            <Button danger onClick={confirmDeleteHandler}>
              Evet
            </Button>
          </React.Fragment>
        }
      >
        <p>{ItemTypeTR} silme işlemini onaylıyor musunuz? Bu işlem geri alınamaz.</p>
      </Modal>

      <Snackbar
        open={openError}
        autoHideDuration={2000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert onClose={handleClose} severity="error" sx={{ width: "100%" }}>
          Ücret bilgisi sayısal bir değer olmalıdır!
        </MuiAlert>
      </Snackbar>

      {!isLoading && loadedItem && (
        <form key={itemstate} className="item-form" onSubmit={itemUpdateSubmitHandler}>
          <div className="button-container">
            <Button type="button" danger onClick={props.onClose}>
              &#60; Geri
            </Button>
          </div>

          <Input
            id="name"
            element="input"
            type="text"
            label="İsim"
            validators={[VALIDATOR_REQUIRE()]}
            errorText="İsim giriniz."
            initialValue={loadedItem.name}
            initialValid={true}
            onInput={inputHandler}
          />
          <Input
            id="name_EN"
            element="input"
            type="text"
            label="İsim (İngilizce)"
            validators={[VALIDATOR_NONE()]}
            errorText="İsim giriniz."
            initialValue={loadedItem.name_EN}
            initialValid={true}
            onInput={inputHandler}
          />
          <Input
            id="name_RU"
            element="input"
            type="text"
            label="İsim (Rusça)"
            validators={[VALIDATOR_NONE()]}
            errorText="İsim giriniz."
            initialValue={loadedItem.name_RU}
            initialValid={true}
            onInput={inputHandler}
          />

          <Input
            id="amount"
            element="input"
            type="text"
            label="Ücret (1-5 kişi)"
            validators={[VALIDATOR_REQUIRE()]}
            errorText="Ücret giriniz."
            initialValue={loadedItem.amount}
            initialValid={true}
            onInput={inputHandler}
          />
          <Input
            id="amount_5_10"
            element="input"
            type="text"
            label="Ücret (5-10 kişi)"
            validators={[VALIDATOR_REQUIRE()]}
            errorText="Ücret giriniz."
            initialValue={loadedItem.amount_5_10}
            initialValid={true}
            onInput={inputHandler}
          />
          <Input
            id="amount_VIP"
            element="input"
            type="text"
            label="Ücret (1-5 kişi, VIP)"
            validators={[VALIDATOR_REQUIRE()]}
            errorText="Ücret giriniz."
            initialValue={loadedItem.amount_VIP}
            initialValid={true}
            onInput={inputHandler}
          />

          <div className="button-container">
            <Button type="submit" disabled={!formState.isValid}>
              Güncelle
            </Button>

            <Button type="button" danger onClick={showDeleteWarningHandler}>
              SİL
            </Button>
          </div>
        </form>
      )}
    </React.Fragment>
  );
};

export default TransferRegionItem;
